<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import V2ReservationShiftModal from '@/components/modals/reservations/V2ReservationShiftModal.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import V2ReservationSlotsPreview from './V2ReservationSlotsPreview.vue';
  import type { IPreviewShift } from './V2ReservationSlotsPreview.vue';
  import { useModal } from '@/composition/use-modal';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IReservationSettings,
    IReservationSettingsConfiguration,
    IReservationShift,
  } from '@/interfaces/IReservationSettings';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    /** General-pane draft: the card strips and the modal preview follow its table times. */
    settings: IReservationSettings;
    settingsDirty?: boolean;
  }>();
  const emit = defineEmits<{ (event: 'saved'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const { width } = useStableWindowSize();
  const deleting = ref<number | null>(null);
  const error = ref('');
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  watch(
    () => props.configuration.settings.branchId,
    () => {
      deleting.value = null;
      error.value = '';
    }
  );
  const tr = (key: string) => String(proxy.$t(key));
  const weekdays = Globals.days.filter((day) => day.val < 7);
  function dayLetter(day: { val: number; name: string }) {
    return proxy.$i18n.locale.startsWith('es') && day.val === 3
      ? 'X'
      : tr(day.name).slice(0, 1).toLocaleUpperCase(proxy.$i18n.locale);
  }
  const shifts = computed(() =>
    [...props.configuration.shifts].sort(
      (a, b) => a.sort - b.sort || a.id - b.id
    )
  );
  /** The arrival times this one turno offers, as the guest will see them. */
  function previewFor(shift: IReservationShift): IPreviewShift[] {
    return [
      {
        key: shift.id,
        name: null,
        from: shift.from.slice(0, 5),
        to: shift.to.slice(0, 5),
        arrivalTimes: shift.arrivalTimes,
        slotIntervalMin: shift.slotIntervalMin,
      },
    ];
  }
  function edit(shift?: IReservationShift) {
    modals.show(
      V2ReservationShiftModal,
      {
        configuration: props.configuration,
        settings: props.settings,
        settingsDirty: props.settingsDirty,
        shift,
      },
      {
        name: 'v2-reservation-shift',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 520,
        maxWidth: Math.round(width.value * 0.95),
        clickToClose: false,
      },
      {
        saved: () => {
          if (alive) emit('saved');
        },
      }
    );
  }
  async function remove(shift: IReservationShift) {
    const configuration = props.configuration;
    if (deleting.value !== null) return;
    const accepted = await modals.confirm({
      title: tr('reservations.shifts.deleteTitle'),
      message: tr('reservations.shifts.deleteMessage'),
      acceptText: tr('delete'),
      acceptColor: 'error',
    });
    if (
      !accepted ||
      !alive ||
      props.configuration.settings.restaurantId !==
        configuration.settings.restaurantId ||
      props.configuration.settings.branchId !==
        configuration.settings.branchId ||
      store.restaurant?.id !== configuration.settings.restaurantId ||
      store.selectedBranch?.id !== configuration.settings.branchId
    )
      return;
    deleting.value = shift.id;
    error.value = '';
    try {
      await ReservationsService.deleteShift(shift.id);
      if (
        !alive ||
        props.configuration.settings.restaurantId !==
          configuration.settings.restaurantId ||
        props.configuration.settings.branchId !==
          configuration.settings.branchId ||
        store.restaurant?.id !== configuration.settings.restaurantId ||
        store.selectedBranch?.id !== configuration.settings.branchId
      )
        return;
      deleting.value = null;
      ui.showToast(AlertType.Success, tr('reservations.shifts.deleted'));
      emit('saved');
    } catch (cause) {
      if (
        !alive ||
        props.configuration.settings.restaurantId !==
          configuration.settings.restaurantId ||
        props.configuration.settings.branchId !==
          configuration.settings.branchId ||
        store.restaurant?.id !== configuration.settings.restaurantId ||
        store.selectedBranch?.id !== configuration.settings.branchId
      )
        return;
      deleting.value = null;
      error.value = tr('reservations.shifts.error');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <PosUiSettingsCard
    class="reservation-shifts-settings"
    flush
    :title="$t('reservations.settings.nav.shifts')"
    :description="$t('reservations.shifts.description')"
  >
    <template #actions>
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="plus"
        @click="edit()"
        >{{ $t('reservations.shifts.add') }}</PosUiButton
      >
    </template>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <PosUiEmptyState
      v-if="!shifts.length"
      icon="clock-outline"
      :title="$t('reservations.shifts.emptyTitle')"
      :description="$t('reservations.shifts.emptyDescription')"
      ><template #action
        ><PosUiButton color="neutral" variant="outline" @click="edit()">{{
          $t('reservations.shifts.add')
        }}</PosUiButton></template
      ></PosUiEmptyState
    >
    <div v-else class="shift-list">
      <article v-for="shift in shifts" :key="shift.id" class="shift-card">
        <div class="shift-details">
          <div class="shift-title">
            <h3>{{ shift.name }}</h3>
            <PosUiBadge v-if="shift.depositRequired === true" color="info">{{
              $t('reservations.settings.nav.deposit')
            }}</PosUiBadge>
          </div>
          <div class="days">
            <span
              v-for="day in weekdays"
              :key="day.val"
              class="day"
              :class="{ active: shift.days.includes(day.val) }"
              :title="$t(day.name)"
              :aria-label="$t(day.name)"
              >{{ dayLetter(day) }}</span
            >
          </div>
          <div class="hours">
            <span
              >{{ shift.from.slice(0, 5) }} – {{ shift.to.slice(0, 5) }}</span
            ><span v-if="shift.to < shift.from" class="crossing">{{
              $t('reservations.shifts.crossesMidnight')
            }}</span>
          </div>
          <p>
            {{
              shift.maxCovers
                ? $t('reservations.shifts.maxCovers', {
                    count: shift.maxCovers,
                  })
                : $t('reservations.shifts.noMax')
            }}
          </p>
          <p v-if="!shift.arrivalTimes" class="shift-times">
            {{
              $t('reservations.shifts.everyMinutes', {
                minutes: shift.slotIntervalMin,
              })
            }}
          </p>
          <V2ReservationSlotsPreview
            class="shift-preview"
            :shifts="previewFor(shift)"
            :settings="settings"
            :settings-dirty="settingsDirty"
            :limit="12"
          />
        </div>
        <PosUiPopover
          placement="bottom"
          align="end"
          :disabled="deleting !== null"
          ><template #trigger="{ toggle }"
            ><PosUiButton
              color="neutral"
              variant="ghost"
              icon="dots-horizontal"
              :aria-label="$t('posV2.moreActions')"
              :disabled="deleting !== null"
              @click="toggle" /></template
          ><template #default="{ close }"
            ><div class="actions-menu">
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="pencil-outline"
                block
                @click="
                  close();
                  edit(shift);
                "
                >{{ $t('reservations.detail.edit') }}</PosUiButton
              ><PosUiButton
                color="error"
                variant="ghost"
                icon="delete-outline"
                block
                @click="
                  close();
                  remove(shift);
                "
                >{{ $t('delete') }}</PosUiButton
              >
            </div></template
          ></PosUiPopover
        >
      </article>
    </div>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .reservation-shifts-settings {
    .error {
      @apply tw-m-0 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3 tw-text-sm tw-text-red-600 md:tw-px-6;
    }
    .shift-card {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-5 tw-py-4 md:tw-px-6;
      & + .shift-card {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
    .shift-details {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2;
      p {
        @apply tw-m-0 tw-text-xs tw-text-neutral-500;
      }
    }
    .shift-preview {
      @apply tw-mt-1;
    }
    .shift-title {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
      h3 {
        @apply tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
    }
    .days {
      @apply tw-flex tw-flex-wrap tw-gap-1;
    }
    .day {
      @apply tw-rounded-md tw-bg-neutral-100 tw-px-1.5 tw-py-1 tw-text-xs tw-text-neutral-400;
      &.active {
        @apply tw-bg-neutral-200 tw-font-medium tw-text-neutral-800;
      }
    }
    .hours {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-tabular-nums tw-text-neutral-800;
    }
    .crossing {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .actions-menu {
      @apply tw-grid tw-gap-1;
    }
  }
</style>
